import * as Sentry from '@sentry/react';
import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { createHttpClient } from 'chaika-http-client';
import queryString from 'query-string';
import compact from 'lodash/compact';

import { store } from '@store';
import { mockAuthUser } from '@store/AuthUser/mock/constants';

declare module 'axios' {
    interface InternalAxiosRequestConfig {
        metadata?: { startTime: number };
    }
    interface AxiosRequestConfig {
        skipForbiddenPopup?: boolean;
    }
}

const UNAUTHORISED_CODE = 401;
const FORBIDDEN_CODE = 403;
const GATEWAY_TIMEOUT = 504;

const PROCESSED_BY_INTERCEPTOR = Symbol('chaika-axios-processed');

// Маркер: ошибка уже отправлена в Sentry из interceptor. beforeSend в app.ts
// дропает события, у которых originalException несёт этот флаг — чтобы повторный
// capture (например, через ErrorBoundary.componentDidCatch) не плодил дубль.
export const SENTRY_ALREADY_REPORTED = '__chaikaSentryReported';

const BASE_URL: string = process.env.API_URL ?? '';

const baseHeaders: Record<string, string> = {
    'Cache-Control': 'cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
};

if (process.env.CMS_ENV === 'test') {
    baseHeaders['auth-user'] = JSON.stringify(mockAuthUser);
}

const instance = createHttpClient({
    tier: 'client-bff',
    baseURL: BASE_URL,
    headers: baseHeaders,
    adapter: process.env.CMS_ENV === 'test' ? 'http' : undefined,
});

export function normalizeUrlPattern(url: string): string {
    const path = url.split('?')[0];
    return path
        .split('/')
        .map(segment => {
            if (!segment) return segment;
            if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
                return ':id';
            }
            if (/^\d+$/.test(segment)) {
                return ':id';
            }
            if (/^[A-Za-z0-9_-]{20,}$/.test(segment)) {
                return ':id';
            }
            return segment;
        })
        .join('/');
}

function isProcessed(target: unknown): boolean {
    return Boolean(
        target && typeof target === 'object' && (target as Record<symbol, unknown>)[PROCESSED_BY_INTERCEPTOR],
    );
}

function markProcessed(target: object): void {
    (target as Record<symbol, unknown>)[PROCESSED_BY_INTERCEPTOR] = true;
}

function reportNetworkErrorToSentry(error: AxiosError): boolean {
    if (axios.isCancel(error)) return false;
    if (!axios.isAxiosError(error)) return false;

    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    const status = error.response?.status;
    const is5xx = typeof status === 'number' && status >= 500 && status <= 599;
    const isNetworkError = !error.response && !isTimeout;

    if (!isTimeout && !is5xx && !isNetworkError) return false;

    if (isNetworkError && typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
    }

    const config = error.config;
    const method = (config?.method ?? 'unknown').toUpperCase();
    const url = config?.url ?? '';
    const urlPattern = normalizeUrlPattern(url);
    const startTime = config?.metadata?.startTime;
    const elapsedMs = startTime !== undefined ? Date.now() - startTime : undefined;
    // axios-retry уменьшает config.timeout между попытками (shouldResetTimeout: false),
    // поэтому для отчёта о контракте используем дефолт инстанса, а не текущее значение конфига.
    const timeoutMs = instance.defaults.timeout;
    const retryState = (config as Record<string, any> | undefined)?.['axios-retry'];
    const retryCount: number = retryState?.retryCount ?? 0;

    const tags: Record<string, string> = {
        http_method: method,
        http_url_pattern: urlPattern,
        http_status: isTimeout ? 'timeout' : status ? String(status) : 'network_error',
    };
    if (isTimeout) tags.http_timeout = 'true';
    if (isNetworkError) tags.http_network_error = 'true';

    const kind = isTimeout ? 'timeout' : is5xx ? `HTTP ${status}` : 'network error';
    const wrapper = new Error(`[axios] ${kind} ${method} ${urlPattern}`);
    wrapper.name = 'AxiosNetworkIssue';
    (wrapper as Error & { cause?: unknown }).cause = error;

    Sentry.captureException(wrapper, {
        tags,
        extra: {
            url,
            baseURL: config?.baseURL,
            method,
            params: config?.params,
            hasData: Boolean(config?.data),
            elapsedMs,
            timeoutMs,
            retryCount,
            code: error.code,
            axiosMessage: error.message,
            status,
            wasOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        },
        fingerprint: isTimeout
            ? ['http-timeout', method, urlPattern]
            : is5xx
              ? ['http-error', method, urlPattern, String(status)]
              : ['http-network', method, urlPattern],
    });

    return true;
}

instance.interceptors.request.use(config => {
    config.metadata = { startTime: Date.now() };
    return config;
});

// Для разработки чтобы отклавливать дублирующиеся запросы
if (process.env.CMS_ENV === 'local-dev1') {
    const requestLog = new Map<string, string>();
    const excludePaths = ['schedule'];

    instance.interceptors.request.use(config => {
        if (config.method === 'get' && excludePaths.every(path => !(config.url || '').includes(path))) {
            const query = queryString.stringify(config.params);
            const key = `${config.url}?${query}`;
            const currentTime = new Date().toISOString();

            if (requestLog.has(key)) {
                console.warn(`Duplicate request detected: ${key}`, {
                    previousTime: requestLog.get(key),
                    currentTime,
                });
            }
            requestLog.set(key, currentTime);
        }
        return config;
    });
}

instance.interceptors.response.use(
    response => response,
    error => {
        if (isProcessed(error)) {
            return Promise.reject(error);
        }

        let reportedToSentry = false;
        if (error && typeof error === 'object') {
            try {
                reportedToSentry = reportNetworkErrorToSentry(error as AxiosError);
            } catch {
                // Sentry-вызов не должен влиять на цепочку ошибок запроса
            }
        }

        let errorData = error.response?.data ?? error;
        errorData = errorData.data?.code ? errorData.data : errorData;
        if (error.response?.status === UNAUTHORISED_CODE) {
            window.location.pathname = '/';
        }

        if (error.response?.status === FORBIDDEN_CODE && !error.config?.skipForbiddenPopup) {
            store.app.forbiddenResources = true;
        }

        store.app.isOnLoad = false;

        if (error.response?.status === GATEWAY_TIMEOUT) {
            return Promise.reject(error.message);
        }

        const errorMessage = compact([
            errorData.message,
            typeof error.response?.data === 'string' ? error.response?.data : undefined,
            error.message !== 'Request failed with status code 500' ? error.message : undefined,
            error.url?.includes('api/auth/time-left') ? 'Get time left error' : undefined,
        ]).join('; ');

        const result = new Error(errorMessage);
        result.name = errorData.code ? errorData.code : error.name;
        if (errorData.payload !== undefined) {
            (result as unknown as Record<string, unknown>).payload = errorData.payload;
        }
        markProcessed(result);
        if (reportedToSentry) {
            (result as unknown as Record<string, unknown>)[SENTRY_ALREADY_REPORTED] = true;
        }
        return Promise.reject(result);
    },
);

export { instance };
export type { AxiosResponse };
